import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EnvironmentService } from '@nomoh-monorepo/shared-data-access-environment';
import { IEstabelecimento, ISetupPrerequisitos } from '@nomoh-monorepo/shared-types';
import { firstValueFrom } from 'rxjs';
import { EstabelecimentoDataService } from './estabelecimento.dataservice';

/**
 * Service HTTP para Estabelecimento
 * Padrão: async/await com firstValueFrom + atualiza DataService
 */
@Injectable({
  providedIn: 'root',
})
export class EstabelecimentoService {
  private http = inject(HttpClient);
  private env = inject(EnvironmentService);
  private dataService = inject(EstabelecimentoDataService);


  private get baseUrl(): string {
    return `${this.env.getApiUrl('bardo')}/estabelecimento`;
  }

  /**
   * Buscar todos os estabelecimentos
   */
  async findAll(
    filtro?: Record<string, string | number | boolean | undefined>
  ): Promise<IEstabelecimento[]> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      let params = new HttpParams();
      if (filtro) {
        Object.entries(filtro).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params = params.set(key, String(value));
          }
        });
      }

      const estabelecimentos = await firstValueFrom(
        this.http.get<IEstabelecimento[]>(`${this.baseUrl}/findAll`, {
          params,
        })
      );

      this.dataService.setEstabelecimentos(estabelecimentos);
      return estabelecimentos;
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao buscar estabelecimentos';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  /**
   * Buscar estabelecimento completo
   */
  async findCompleto(): Promise<IEstabelecimento | null> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      const estabelecimento = await firstValueFrom(
        this.http.get<IEstabelecimento | null>(`${this.baseUrl}/findCompleto`)
      );

      if (estabelecimento) {
        this.dataService.setEstabelecimentos([estabelecimento]);
        this.dataService.setEstabelecimentoAtual(estabelecimento);
      } else {
        this.dataService.setEstabelecimentos([]);
        this.dataService.setEstabelecimentoAtual(null);
      }
      return estabelecimento;
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao buscar estabelecimento completo';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  /**
   * Buscar estabelecimento por ID
   */
  async findById(
    _id: string,
    options: { silent?: boolean } = {}
  ): Promise<IEstabelecimento> {
    const controlarLoading = !options.silent;
    try {
      if (controlarLoading) {
        this.dataService.setLoading(true);
        this.dataService.limparErro();
      }

      // Endpoint /find retorna apenas os dados essenciais para a tela principal
      const estabelecimento = await firstValueFrom(
        this.http.get<IEstabelecimento>(`${this.baseUrl}/find`, {
          params: { _: Date.now().toString() },
        })
      );

      this.dataService.setEstabelecimentoAtual(estabelecimento);
      return estabelecimento;
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao buscar estabelecimento';
      if (!controlarLoading) {
        throw error;
      }
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      if (controlarLoading) {
        this.dataService.setLoading(false);
      }
    }
  }

  /**
   * Criar novo estabelecimento
   */
  async criar(estabelecimento: Partial<IEstabelecimento>): Promise<string> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      const id = await firstValueFrom(
        this.http.post<string>(`${this.baseUrl}/create`, estabelecimento)
      );

      // Recarregar lista após criar
      await this.findAll();

      return id;
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao criar estabelecimento';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  /**
   * Atualizar estabelecimento existente
   */
  async atualizar(
    id: string,
    estabelecimento: Partial<IEstabelecimento>
  ): Promise<void> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      const payload: Partial<IEstabelecimento> & { id: string } = {
        ...estabelecimento,
        id,
      };

      await firstValueFrom(
        this.http.put<void>(`${this.baseUrl}/update`, payload)
      );

      // Atualizar no state
      this.dataService.updateEstabelecimento(id, estabelecimento);

      // Recarregar dados oficiais para garantir sincronismo
      await this.findById(id, { silent: true });

      // Reaplicar imagens do payload: no mobile o GET /find pode retornar cache antigo
      if (estabelecimento.imagens) {
        this.dataService.updateEstabelecimento(id, {
          imagens: estabelecimento.imagens,
        });
      }
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao atualizar estabelecimento';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  /**
   * Deletar estabelecimento
   */
  async deletar(id: string): Promise<void> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      await firstValueFrom(this.http.delete<void>(`${this.baseUrl}/${id}`));

      // Remover do state
      this.dataService.removeEstabelecimento(id);
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao deletar estabelecimento';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  /**
   * Ativar estabelecimento
   */
  async ativar(id: string): Promise<void> {
    await this.atualizar(id, { status: true });
  }

  /**
   * Desativar estabelecimento
   */
  async desativar(id: string): Promise<void> {
    await this.atualizar(id, { status: false });
  }

  /**
   * Flags leves de pré-requisitos (gate WhatsApp / Novo Agendamento).
   */
  async getSetupPrerequisitos(): Promise<ISetupPrerequisitos> {
    return firstValueFrom(
      this.http.get<ISetupPrerequisitos>(
        `${this.baseUrl}/setup-prerequisitos`
      )
    );
  }

  async getPlanoQuotas(): Promise<{
    nome?: string;
    maxProfissionais?: number | null;
    allowRecepcao?: boolean;
    allowAdminExtra?: boolean;
    hasZapIntegration?: boolean;
  }> {
    const quotas = await firstValueFrom(
      this.http.get<{
        nome?: string;
        maxProfissionais?: number | null;
        allowRecepcao?: boolean;
        allowAdminExtra?: boolean;
        hasZapIntegration?: boolean;
      }>(`${this.baseUrl}/plano-quotas`)
    );
    this.dataService.setPlanoQuotas(quotas);
    return quotas;
  }

  async buscarTiposFluxoAtendimento(token: string): Promise<string> {
    try {
      let params = new HttpParams();
      params = params.set('token', token);

      const response = await firstValueFrom(
        this.http.get<{ id: string | null }>(
          `${this.env.getApiUrl('nomoh')}/fluxo-cliente/findByToken`,
          { params }
        )
      );
      if (!response?.id) {
        throw new Error('Tipo de fluxo de atendimento não encontrado');
      }

      return response.id;
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao buscar tipos de fluxo de atendimento';
      this.dataService.setErro(mensagem);
      throw error;
    }
  }
}
